import { alpha, useTheme } from '@mui/material/styles';
import { arrayOf, func, number, string } from 'prop-types';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

/** Spacing between bars */
const BAR_GAP = 1.5;
const MIN_BAR_HEIGHT = 2;
const STRIP_HEIGHT = 28;
const MAX_BAR_HEIGHT = STRIP_HEIGHT - 4;
const SCRUBBER_SIZE = 11;
const SCRUBBER_TOP = Math.round((STRIP_HEIGHT - SCRUBBER_SIZE) / 2);
const TRACK_VERTICAL_CENTER = SCRUBBER_TOP + SCRUBBER_SIZE / 2;
const LINE_STROKE = 2.5;

function computeEqualBarLayout(stripWidth, n, preferredGap) {
  if (stripWidth <= 0 || n < 1) {
    return { barW: 0, gap: preferredGap, trackW: 0 };
  }
  let gap = preferredGap;
  while (gap >= 0) {
    const inner = stripWidth - (n - 1) * gap;
    if (inner > 0) {
      return { barW: inner / n, gap, trackW: stripWidth };
    }
    if (gap === 0) break;
    gap -= 1;
  }
  return { barW: stripWidth / n, gap: 0, trackW: stripWidth };
}

const VoiceMessageWaveform = ({
  variant = 'bars',
  heights,
  progress,
  onSeekProgress,
  onScrubStart,
  onScrubEnd,
  playedBarColor = null,
  unplayedBarColor = null,
  scrubberColor = null,
}) => {
  const theme = useTheme();
  const containerRef = useRef(null);
  const [stripWidth, setStripWidth] = useState(0);
  const stripWidthRef = useRef(1);
  const scrubbingRef = useRef(false);
  const onSeekProgressRef = useRef(onSeekProgress);
  const onScrubStartRef = useRef(onScrubStart);
  const onScrubEndRef = useRef(onScrubEnd);

  useEffect(() => {
    onSeekProgressRef.current = onSeekProgress;
    onScrubStartRef.current = onScrubStart;
    onScrubEndRef.current = onScrubEnd;
  }, [onSeekProgress, onScrubStart, onScrubEnd]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) {
        const w = entry.contentRect.width;
        stripWidthRef.current = w;
        setStripWidth(w);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  /** Flex layout often reports 0 width on first paint; re-measure after commit + next frames. */
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    let alive = true;
    const measure = () => {
      if (!alive) return;
      const w = el.getBoundingClientRect().width;
      if (w > 0) {
        stripWidthRef.current = w;
        setStripWidth(prev => (Math.abs(prev - w) > 0.5 ? w : prev));
      }
    };
    measure();
    const id1 = requestAnimationFrame(() => {
      measure();
      if (alive) requestAnimationFrame(measure);
    });
    return () => {
      alive = false;
      cancelAnimationFrame(id1);
    };
  }, [variant, heights.length]);

  const progressFromX = useCallback(clientX => {
    const el = containerRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const rel = clientX - rect.left;
    const w = stripWidthRef.current || 1;
    return Math.max(0, Math.min(1, rel / w));
  }, []);

  const handlePointerDown = useCallback(
    e => {
      e.preventDefault();
      scrubbingRef.current = true;
      onScrubStartRef.current();
      onSeekProgressRef.current(progressFromX(e.clientX));

      const handleMove = ev => {
        if (scrubbingRef.current) {
          onSeekProgressRef.current(progressFromX(ev.clientX));
        }
      };
      const handleUp = () => {
        scrubbingRef.current = false;
        onScrubEndRef.current();
        document.removeEventListener('pointermove', handleMove);
        document.removeEventListener('pointerup', handleUp);
      };
      document.addEventListener('pointermove', handleMove);
      document.addEventListener('pointerup', handleUp);
    },
    [progressFromX],
  );

  const clampedProgress = Math.max(0, Math.min(1, Number.isFinite(progress) ? progress : 0));
  /** WhatsApp-like bars: dark gray played, light gray unplayed; scrubber uses theme primary. */
  const played =
    playedBarColor ||
    (theme.palette.mode === 'dark' ? theme.palette.grey[300] : theme.palette.grey[800]);
  const unplayed =
    unplayedBarColor ||
    (theme.palette.mode === 'dark'
      ? alpha(theme.palette.grey[400], 0.55)
      : alpha(theme.palette.grey[700], 0.45));
  const scrubber = scrubberColor || theme.palette.primary.main;

  const containerStyle = useMemo(
    () => ({
      flex: 1,
      minWidth: 48,
      minHeight: STRIP_HEIGHT + 8,
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      position: 'relative',
      touchAction: 'none',
      userSelect: 'none',
    }),
    [],
  );

  if (variant === 'line') {
    const trackW = stripWidth;
    const playheadX = clampedProgress * trackW;

    return (
      <div ref={containerRef} style={containerStyle} onPointerDown={handlePointerDown}>
        <div style={{ position: 'relative', width: '100%', height: STRIP_HEIGHT }}>
          {trackW > 0 && (
            <svg width={trackW} height={STRIP_HEIGHT} style={{ display: 'block' }}>
              <line
                x1={0}
                y1={TRACK_VERTICAL_CENTER}
                x2={trackW}
                y2={TRACK_VERTICAL_CENTER}
                stroke={unplayed}
                strokeWidth={LINE_STROKE}
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
              {clampedProgress > 0 && (
                <line
                  x1={0}
                  y1={TRACK_VERTICAL_CENTER}
                  x2={playheadX}
                  y2={TRACK_VERTICAL_CENTER}
                  stroke={played}
                  strokeWidth={LINE_STROKE}
                  strokeLinecap="round"
                />
              )}
            </svg>
          )}
          {trackW > 0 && (
            <div
              style={{
                position: 'absolute',
                width: SCRUBBER_SIZE,
                height: SCRUBBER_SIZE,
                borderRadius: SCRUBBER_SIZE / 2,
                top: SCRUBBER_TOP,
                left: playheadX,
                transform: 'translateX(-50%)',
                backgroundColor: scrubber,
                pointerEvents: 'none',
              }}
            />
          )}
        </div>
      </div>
    );
  }

  const n = heights.length;
  const { barW, gap: barGap, trackW } = computeEqualBarLayout(stripWidth, n, BAR_GAP);
  const seekW = trackW > 0 ? trackW : Math.max(1, stripWidth);
  /** Same X for scrubber center and played/unplayed split — float, no rounding (avoids offset at 0 and clunky motion). */
  const playheadX = clampedProgress * seekW;

  return (
    <div ref={containerRef} style={containerStyle} onPointerDown={handlePointerDown}>
      <div style={{ position: 'relative', width: trackW || '100%', height: STRIP_HEIGHT }}>
        {trackW > 0 && n > 0 && barW > 0 && (
          <svg width={trackW} height={STRIP_HEIGHT} style={{ display: 'block' }}>
            {heights.map((h, index) => {
              const x = index * (barW + barGap);
              const barH = MIN_BAR_HEIGHT + (MAX_BAR_HEIGHT - MIN_BAR_HEIGHT) * h;
              const barCenterX = x + barW / 2;
              const isPlayed = barCenterX <= playheadX;
              const y = (STRIP_HEIGHT - barH) / 2;
              const rx = Math.min(barW / 2, barH / 2);
              return (
                <rect
                  key={index}
                  x={x}
                  y={y}
                  width={barW}
                  height={barH}
                  rx={rx}
                  ry={rx}
                  fill={isPlayed ? played : unplayed}
                />
              );
            })}
          </svg>
        )}
        {stripWidth > 0 && trackW > 0 && (
          <div
            style={{
              position: 'absolute',
              width: SCRUBBER_SIZE,
              height: SCRUBBER_SIZE,
              borderRadius: SCRUBBER_SIZE / 2,
              top: SCRUBBER_TOP,
              left: playheadX,
              transform: 'translateX(-50%)',
              backgroundColor: scrubber,
              pointerEvents: 'none',
            }}
          />
        )}
      </div>
    </div>
  );
};

VoiceMessageWaveform.propTypes = {
  variant: string,
  heights: arrayOf(number).isRequired,
  progress: number.isRequired,
  onSeekProgress: func.isRequired,
  onScrubStart: func.isRequired,
  onScrubEnd: func.isRequired,
  playedBarColor: string,
  unplayedBarColor: string,
  scrubberColor: string,
};

export default VoiceMessageWaveform;
